import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, FlatList } from 'react-native';
import { Card, Title, Paragraph, Button, FAB, Dialog, Portal, TextInput, Snackbar, ActivityIndicator, useTheme, IconButton, Divider } from 'react-native-paper';
import { supabase } from '../../services/supabase';

type Props = {
  navigation: any;
};

type Empresa = {
  id?: number;
  nome: string;
  cnpj: string;
  telefone: string;
  email: string;
  dados_empresa?: {
    nome: string;
    cnpj: string;
    telefone: string;
  };
};

export function GerenciarEmpresas({ navigation }: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentEmpresa, setCurrentEmpresa] = useState<Empresa>({
    nome: '',
    cnpj: '',
    telefone: '',
    email: '',
    dados_empresa: {
      nome: '',
      cnpj: '',
      telefone: ''
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      
      // Verificar se o usuário é admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('admin')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.admin) throw new Error('Acesso negado. Você não tem permissão de administrador.');

      // Buscar empresas
      const { data, error } = await supabase
        .from('empresa')
        .select('*')
        .order('nome', { ascending: true });

      if (error) throw error;
      
      setEmpresas(data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!currentEmpresa.nome || !currentEmpresa.cnpj) {
        setError('Nome e CNPJ são campos obrigatórios');
        return;
      }

      // Preparar dados da empresa
      const empresaData = {
        nome: currentEmpresa.nome,
        cnpj: currentEmpresa.cnpj,
        telefone: currentEmpresa.telefone,
        email: currentEmpresa.email,
        dados_empresa: {
          nome: currentEmpresa.nome,
          cnpj: currentEmpresa.cnpj,
          telefone: currentEmpresa.telefone
        }
      };

      let result;
      
      if (isEditing && currentEmpresa.id) {
        // Atualizar empresa existente
        result = await supabase
          .from('empresa')
          .update(empresaData)
          .eq('id', currentEmpresa.id);
      } else {
        // Criar nova empresa
        result = await supabase
          .from('empresa')
          .insert(empresaData);
      }

      if (result.error) throw result.error;
      
      setSuccess(isEditing ? 'Empresa atualizada com sucesso!' : 'Empresa criada com sucesso!');
      setDialogVisible(false);
      loadEmpresas();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      if (!currentEmpresa.id) {
        setError('ID da empresa não encontrado');
        return;
      }

      // Verificar se existem usuários vinculados a esta empresa
      const { data: usuariosVinculados, error: checkError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('empresa', currentEmpresa.id);

      if (checkError) throw checkError;
      
      if (usuariosVinculados && usuariosVinculados.length > 0) {
        throw new Error(`Não é possível excluir esta empresa. Existem ${usuariosVinculados.length} usuários vinculados a ela.`);
      }

      const { error } = await supabase
        .from('empresa')
        .delete()
        .eq('id', currentEmpresa.id);

      if (error) throw error;
      
      setSuccess('Empresa excluída com sucesso!');
      setDeleteDialogVisible(false);
      loadEmpresas();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir empresa');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setCurrentEmpresa({
      nome: '',
      cnpj: '',
      telefone: '',
      email: '',
      dados_empresa: {
        nome: '',
        cnpj: '',
        telefone: ''
      }
    });
    setIsEditing(false);
    setDialogVisible(true);
  };

  const openEditDialog = (empresa: Empresa) => {
    setCurrentEmpresa(empresa);
    setIsEditing(true);
    setDialogVisible(true);
  };

  const openDeleteDialog = (empresa: Empresa) => {
    setCurrentEmpresa(empresa);
    setDeleteDialogVisible(true);
  };

  const renderItem = ({ item }: { item: Empresa }) => {
    return (
      <Card style={styles.card}>
        <Card.Content>
          <Title>{item.nome}</Title>
          <Divider style={styles.divider} />
          
          <Paragraph>CNPJ: {item.cnpj}</Paragraph>
          <Paragraph>Telefone: {item.telefone || 'Não informado'}</Paragraph>
          <Paragraph>Email: {item.email || 'Não informado'}</Paragraph>
          
          <View style={styles.cardActions}>
            <IconButton
              icon="pencil"
              size={20}
              onPress={() => openEditDialog(item)}
            />
            <IconButton
              icon="delete"
              size={20}
              onPress={() => openDeleteDialog(item)}
            />
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && empresas.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Title style={styles.title}>Gerenciar Empresas</Title>
      <Paragraph style={styles.subtitle}>Cadastre e gerencie as empresas do sistema</Paragraph>

      {empresas.length === 0 ? (
        <Card style={styles.emptyCard}>
          <Card.Content>
            <Title>Nenhuma empresa cadastrada</Title>
            <Paragraph>Clique no botão + para adicionar uma nova empresa.</Paragraph>
          </Card.Content>
        </Card>
      ) : (
        <FlatList
          data={empresas}
          renderItem={renderItem}
          keyExtractor={(item) => item.id?.toString() || Math.random().toString()}
          contentContainerStyle={styles.listContainer}
        />
      )}

      <FAB
        style={styles.fab}
        icon="plus"
        onPress={openAddDialog}
      />

      <Portal>
        {/* Dialog para adicionar/editar empresa */}
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)}>
          <Dialog.Title>{isEditing ? 'Editar Empresa' : 'Nova Empresa'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nome da Empresa"
              value={currentEmpresa.nome}
              onChangeText={(text) => setCurrentEmpresa(prev => ({ ...prev, nome: text }))}
              mode="outlined"
              style={styles.input}
            />
            
            <TextInput
              label="CNPJ"
              value={currentEmpresa.cnpj}
              onChangeText={(text) => setCurrentEmpresa(prev => ({ ...prev, cnpj: text }))}
              mode="outlined"
              style={styles.input}
              keyboardType="numeric"
            />
            
            <TextInput
              label="Telefone"
              value={currentEmpresa.telefone}
              onChangeText={(text) => setCurrentEmpresa(prev => ({ ...prev, telefone: text }))}
              mode="outlined"
              style={styles.input}
              keyboardType="phone-pad"
            />
            
            <TextInput
              label="Email"
              value={currentEmpresa.email}
              onChangeText={(text) => setCurrentEmpresa(prev => ({ ...prev, email: text }))}
              mode="outlined"
              style={styles.input}
              keyboardType="email-address"
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleSave} loading={loading}>Salvar</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Dialog para confirmar exclusão */}
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Confirmar Exclusão</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Tem certeza que deseja excluir a empresa "{currentEmpresa.nome}"?</Paragraph>
            <Paragraph style={styles.warningText}>Esta ação não pode ser desfeita.</Paragraph>
            <Paragraph style={styles.warningText}>Atenção: A empresa só pode ser excluída se não houver usuários vinculados a ela.</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleDelete} color="#F44336" loading={loading}>Excluir</Button>
          </Dialog.Actions>
        </Dialog>

        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>

        <Snackbar
          visible={!!success}
          onDismiss={() => setSuccess(null)}
          duration={3000}
          style={{ backgroundColor: '#4CAF50' }}
        >
          {success}
        </Snackbar>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#666',
  },
  listContainer: {
    paddingBottom: 80, // Espaço para o FAB
  },
  card: {
    marginBottom: 16,
    elevation: 4,
  },
  divider: {
    marginVertical: 8,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  emptyCard: {
    marginTop: 20,
    backgroundColor: '#f5f5f5',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  input: {
    marginBottom: 12,
  },
  warningText: {
    color: '#F44336',
    marginTop: 8,
  },
});
